import { createContext, useContext, type ReactNode } from 'react'
import { useAuth } from './useAuth'
import type { User } from '@/types/auth'
import { MfaLoginDialog } from '@/components/auth/MfaLoginDialog'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  error: string | null
  mfaPending: boolean
  login: () => void
  logout: () => void
  getToken: () => Promise<string | null>
  hasRole: (role: string) => boolean
  verifyMfa: (code: string, isBackupCode?: boolean) => Promise<boolean>
  cancelMfa: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth()

  return (
    <AuthContext.Provider value={auth}>
      {children}
      {/* MFA verification dialog - shown when Keycloak auth succeeds but MFA is required */}
      <MfaLoginDialog
        open={auth.mfaPending}
        userEmail={auth.user?.email}
        onVerify={auth.verifyMfa}
        onCancel={auth.cancelMfa}
      />
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
